import { RouterStore } from '@estee/elc-router';
import { BrowserStorage } from '@estee/elc-universal-utils';

import { ILoadSocialProfileResponse, SocialSignUpType, UserStatusKey } from '~api/RampPgApiSdk';
import { MessageType, SocialProfileSearchParams, StorageKey } from '~constants/Constants';
import { ISocialSignUpProvider } from '~domain/entities/Config';
import { ConfigRepository } from '~repositories/ConfigRepository';
import { MessageGroup, MessageRepository } from '~repositories/MessageRepository';
import { TranslationRepository } from '~repositories/TranslationRepository';
import { UserRepository } from '~repositories/UserRepository';
import { ACCOUNT_SIGN_IN_ROUTE, SIGNUP_ROUTE } from '~routes/routes';
import { diContainer } from '~setup/diContainer';
import serviceNames from '~setup/RampService';
import { PrefillUserFormUseCase } from '~use-cases/PrefillUserFormUseCase';
import { TaggingEventSourceType } from '~utils/Analytics';
import { queryStringToJson } from '~utils/QueryString';

import { ConfigurationLoadedUseCase } from '../ConfigurationLoadedUseCase';

export class SocialSignUpCallbackLoadedUseCase {
    private userRepository: UserRepository;
    private configRepository: ConfigRepository;
    private routerStore: RouterStore;
    private messageRepository: MessageRepository;
    private translationRepository: TranslationRepository;

    constructor() {
        this.userRepository = diContainer.get(serviceNames.userRepository);
        this.configRepository = diContainer.get(serviceNames.configRepository);
        this.routerStore = diContainer.get(serviceNames.routerStore);
        this.messageRepository = diContainer.get(serviceNames.messageRepository);
        this.translationRepository = diContainer.get(serviceNames.translationRepository);
    }

    execute = async () => {
        try {
            const queryString = window.location.search;
            const queryParams = new URLSearchParams(queryString);

            const provider = queryParams.get(SocialProfileSearchParams.PROVIDER);
            if (!provider) {
                throw new Error('The social provider is missing from url parameter.');
            }

            this.onProviderIsReady(provider);

            // load social sign up provider configuration from common section
            const providerConfiguration = this.getSocialSignUpProviderConfiguration(
                provider as SocialSignUpType
            );

            // load social sign up node: kakaoGiftSocialSignUp or ssgSocialSignUp
            await this.loadConfiguration(queryParams);

            const queryParamsObject = queryStringToJson(queryString);
            const isExistingUser = await this.checkExistingUser(
                queryParamsObject,
                providerConfiguration
            );

            const profile = await this.userRepository.loadSocialProfile(provider);

            if (isExistingUser) {
                const { displayName } = providerConfiguration;
                // redirect to sign in page
                this.redirectToSignInPage(profile, displayName);

                return;
            }

            await this.displaySocialSignUpPage(providerConfiguration, profile, queryParamsObject);
        } catch (e) {
            throw new Error(`There was an error when loading social sign up page: ${e}`);
        }
    };

    private prefillEmailForExistingEmailCompletedUser = (profile: ILoadSocialProfileResponse) => {
        const {
            accountSignInFormStore,
            consentForm: { hasCompleteEmailAccount }
        } = this.userRepository;
        hasCompleteEmailAccount &&
            accountSignInFormStore.setFieldValue(
                'accountIdentifier',
                // email/mobile are currently readonly in social-sign-up flow
                profile?.emailAddress || profile?.mobileNumber || ''
            );
    };

    private onProviderIsReady = (provider: string) => {
        this.configRepository.setSocialSignUpType(provider || '');
        this.setEventSource(provider);
        BrowserStorage.setItem(
            StorageKey.SOCIAL_SIGN_UP_PROVIDER_SOURCE,
            provider,
            'sessionStorage'
        );
    };

    private loadConfiguration = async (queryParams: URLSearchParams) => {
        const configNodeName = queryParams.get(SocialProfileSearchParams.CONFIG_NODE_NAME);
        if (!configNodeName) {
            throw new Error('Configuration node is missing from url parameter.');
        }
        await new ConfigurationLoadedUseCase(`ramp.${configNodeName}`).execute();
    };

    private getSocialSignUpProviderConfiguration = (provider: SocialSignUpType) => {
        const socialSignUpProviderConfig =
            provider && this.configRepository.getSocialSignUpProvider(provider);
        if (!socialSignUpProviderConfig) {
            throw new Error(
                'Loading configuration failed or configuration is missing for social sign up.'
            );
        }

        return socialSignUpProviderConfig;
    };

    private setEventSource = (provider: string) => {
        // map social provider source name to event tracking source name
        const mappings = {
            [SocialSignUpType.KAKAO_GIFT]: TaggingEventSourceType.KAKAO_GIFT,
            [SocialSignUpType.SSG]: TaggingEventSourceType.SSG
        };
        const eventSource = mappings[provider as SocialSignUpType];
        if (eventSource) {
            const { setSignUpEventSource, setSignInEventSource } = this.userRepository;
            setSignUpEventSource(eventSource);
            // social sign-up allows sign-in with social providers like kakao/naver
            setSignInEventSource(eventSource);
        }
    };

    private redirectToSignInPage = (profile: ILoadSocialProfileResponse, displayName: string) => {
        this.prefillEmailForExistingEmailCompletedUser(profile);
        const {
            translations: { signInToConnectSocial }
        } = this.translationRepository;
        this.setMessage(signInToConnectSocial, displayName, MessageGroup.SIGN_IN);

        this.routerStore.changeCurrentRoute(ACCOUNT_SIGN_IN_ROUTE);
    };

    private displaySocialSignUpPage = async (
        { messageKey, displayName, providerName }: ISocialSignUpProvider,
        profile: ILoadSocialProfileResponse,
        queryParamsObject: Record<string, string>
    ) => {
        const socialSignUpMessage = this.translationRepository.translations[messageKey];
        this.setMessage(socialSignUpMessage, displayName, MessageGroup.SIGN_UP);
        await new PrefillUserFormUseCase(providerName, profile).execute();

        this.routerStore.changeCurrentRoute(SIGNUP_ROUTE, undefined, queryParamsObject);
    };

    private setMessage(
        messageTranslation: string,
        displayName: string,
        messageGroup: MessageGroup
    ) {
        if (!messageTranslation) {
            return;
        }

        const socialPlaceholder = '::SOCIAL::';
        let message = messageTranslation;
        if (messageTranslation.match(socialPlaceholder)) {
            message = messageTranslation.replace(socialPlaceholder, displayName);
        } else {
            // If placeholder is replaced by previous social login provider in locale storage
            // in TranslationSetupUtils, need replace it with kkg/ssg
            const socialDisplayName = BrowserStorage.getItem(StorageKey.SOCIAL_DISPLAY_NAME);
            if (socialDisplayName) {
                message = messageTranslation.replace(socialDisplayName, displayName);
            }
        }
        this.messageRepository.clearMessagesByGroup(messageGroup);
        this.messageRepository.setMessages(messageGroup, [{ message, msgType: MessageType.INFO }]);
    }

    private async checkExistingUser(
        queryParamsObject: Record<string, string>,
        { providerParams = [], providerName }: ISocialSignUpProvider
    ) {
        const payload = this.preparePayload(providerParams, queryParamsObject);

        const {
            consentForm: { setUsers, setIsSocialSignUpFlow },
            postSocialSignUpSession
        } = this.userRepository;
        const { users = [], success } = await postSocialSignUpSession({
            ...payload,
            socialProvider: providerName as SocialSignUpType
        });

        if (!success) {
            throw new Error('An error occurred when posting to session API');
        }
        // Will be used to determine if we should prefill email address in sign-in form
        setUsers(users);
        // For account access page to hide the sign-up tab
        setIsSocialSignUpFlow(true);

        return users?.some((user) =>
            [UserStatusKey.EXISTING_COMPLETE, UserStatusKey.EXISTING_COMPLETE_OFFLINE].includes(
                user.userStatusKey
            )
        );
    }

    private preparePayload = (
        providerParams: string[],
        queryParamsObject: Record<string, string>
    ) => {
        return providerParams?.reduce((acc: Record<string, string>, key) => {
            if (queryParamsObject.hasOwnProperty(key)) {
                const code = queryParamsObject[key];
                if (!code) {
                    throw new Error(`URL query parameter ${key}'s value is missing.`);
                }
                acc[key] = code;
            }

            return acc;
        }, {});
    };
}
